import { NEXT_PUBLIC_SITE_URL } from '@src/config/env'

/**
 * Normalizes a canonical URL value that may be authored either as an absolute
 * URL (e.g. from Contentful, possibly cross-collection) or as a site-relative
 * path (e.g. from BFF which strips the domain). Returns an absolute URL with
 * the environment's `NEXT_PUBLIC_SITE_URL` as the origin when the input is
 * relative.
 *
 * - Absolute URLs (`http://...`, `https://...`) are returned verbatim so that
 *   authors can set cross-collection or cross-locale canonicals
 * - Relative paths are joined with `NEXT_PUBLIC_SITE_URL` using a single `/`
 *   separator regardless of whether either side includes one.
 * - Empty / whitespace-only input returns `undefined` so callers can fall
 *   through to auto-generation from the subcollection slug.
 *
 * The `siteUrl` parameter is primarily for unit testing; in production the
 * default pulls from `NEXT_PUBLIC_SITE_URL` which is set per deploy env.
 */
export function absolutizeCanonical(
  urlOrPath: string | undefined | null,
  siteUrl: string = NEXT_PUBLIC_SITE_URL,
): string | undefined {
  if (!urlOrPath) return undefined
  const trimmed = urlOrPath.trim()
  if (!trimmed) return undefined

  if (/^(?:https?:)?\/\//i.test(trimmed)) {
    return trimmed.startsWith('//') ? `https:${trimmed}` : trimmed
  }

  const base = siteUrl.replace(/\/+$/, '')
  const path = trimmed.replace(/^\/+/, '')
  return `${base}/${path}`
}
